import { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check } from '@mui/icons-material';
import { LoadingButtonProps } from '@mui/lab';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, BoxProps, Button, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  Customer,
  CustomerFragmentFragment,
  CustomerUpdateInput,
  RemindingTone,
  useCustomerQuery,
  useCustomersQuery,
  useUpdateCustomerMutation,
} from '../../../api/gql';
import { FullPageLoader } from '../../../components';
import { FormInput, FormInputProps } from '../../../components/form/FormInput';
import { FormSelect, FormSelectProps } from '../../../components/form/FormSelect';
import { useAresSearch } from '../../../hooks/useAresSearch';
import { getRemindingToneTransKey } from '../../../utils/invoice';
import { notify } from '../../../utils/notification';
import { customerValidationSchema } from '../../../utils/validationSchemas';
import { useSafeNavigation } from '../../../hooks/useSafeNavigation';

export const EditCustomer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { safeNavigate } = useSafeNavigation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useCustomerQuery({
    id: id ?? '',
  });

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({ message: t('query.error.default') });
    }
  }, [error, t]);

  const { mutate, isPending: isMutating } = useUpdateCustomerMutation({
    onSuccess: async (res) => {
      notify.positive({ message: t('customer.mutation.success') });
      // invalidate customers query as customer changed
      await queryClient.invalidateQueries({
        queryKey: useCustomersQuery.getKey(),
      });

      if (id) {
        // update customer query with mutation result
        queryClient.setQueryData(useCustomerQuery.getKey({ id }), {
          customer: res.updateCustomer,
        });
      }

      safeNavigate('..');
    },
    onError: () => {
      notify.negative({ message: t('customer.mutation.error') });
    },
  });

  const handleSubmit = (data: Omit<CustomerFragmentFragment, 'id'>) => {
    if (!data.name || !id) {
      throw new Error('Name or ID are required fields');
    }

    const customerDto: CustomerUpdateInput = {
      id: id,
      name: data.name,
      address: data.address,
      email: data.email,
      vatId: data.vatId,
      taxId: data.taxId,
      phoneNumber: data.phoneNumber,
      remindingTone: data.remindingTone,
    };

    mutate({ customer: customerDto });
  };

  return (
    <Box p={4} minHeight={598}>
      <Typography variant={'h1'}>{t('editContact')}</Typography>
      {isLoading ? <FullPageLoader /> : null}
      <CustomerForm
        customer={data?.customer}
        onSubmit={handleSubmit}
        submitButtonProps={{
          loading: isMutating,
        }}
        mt={4}
      />
    </Box>
  );
};

type CustomerFormProps = {
  onSubmit: (data: Omit<CustomerFragmentFragment, 'id'>) => void;
  readonly?: boolean;
  customer?: CustomerFragmentFragment;
  submitButtonProps?: LoadingButtonProps;
} & Omit<BoxProps, 'onSubmit'>;
const CustomerForm = ({ readonly, customer, onSubmit, submitButtonProps, ...rest }: CustomerFormProps) => {
  const { t } = useTranslation();

  const { control, formState, getValues, setValue, reset, getFieldState, handleSubmit } = useForm<
    Omit<Customer, 'id' | '__typename'>
  >({
    resolver: yupResolver(customerValidationSchema),
  });

  const {
    search,
    state: { isPending },
  } = useAresSearch();

  const onAresSearch = async () => {
    const vatId = getValues('vatId');

    if (!vatId) {
      // should not happen due to validation
      return;
    }

    const res = await search({
      vatId: vatId,
    });

    if (res.queryAres) {
      const address = [res.queryAres.streetAddress, res.queryAres.zip, res.queryAres.city, res.queryAres.country]
        .filter((e) => e)
        .join('\n');

      res.queryAres.name ? setValue('name', res.queryAres.name) : null;
      res.queryAres.taxId ? setValue('taxId', res.queryAres.taxId) : null;
      address ? setValue('address', address) : null;

      notify.positive({ message: t('ares.prefill.success') });
      return;
    }

    notify.warning({ message: t('ares.prefill.error') });
  };

  const formInputSharedProps = useMemo(
    () =>
      ({
        size: 'medium',
        variant: 'standard',
        autoComplete: 'off',
        InputProps: {
          readOnly: readonly,
          disableUnderline: readonly,
        },
      }) satisfies Partial<FormSelectProps & FormInputProps>,
    [readonly],
  );

  // Populate Form if data are not yet loaded on component mount
  useEffect(() => {
    if (customer && !formState.defaultValues) {
      reset(customer);
    }
  }, [customer, formState.defaultValues, reset]);

  const shouldShowContactInfoWarning = formState.dirtyFields?.email || formState.dirtyFields?.phoneNumber;
  const vatIdFieldState = getFieldState('vatId', formState);
  const shouldShowRemindingToneWarning = formState.dirtyFields?.remindingTone;

  const phoneNumberFieldState = getFieldState('phoneNumber', formState);
  const emailFieldState = getFieldState('email', formState);

  const shouldShowInvalidContactInfoWarning = phoneNumberFieldState.invalid || emailFieldState.invalid;

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid xs={12} md={6}>
            <FormInput {...formInputSharedProps} control={control} name={'vatId'} label={t('vatId')} />
          </Grid>
          <Grid xs={12} md={6} alignSelf={'center'}>
            <LoadingButton
              disabled={vatIdFieldState.invalid}
              loading={isPending}
              fullWidth
              variant={'outlined'}
              onClick={onAresSearch}
            >
              {t('updateFromAres')}
            </LoadingButton>
          </Grid>
          <Grid xs={12}>
            <FormInput {...formInputSharedProps} control={control} name={'taxId'} label={t('taxId')} />
          </Grid>
          <Grid xs={12}>
            <FormInput {...formInputSharedProps} control={control} name={'name'} label={t('businessName') + '*'} />
          </Grid>
          <Grid xs={12}>
            <FormInput
              {...formInputSharedProps}
              control={control}
              multiline={true}
              minRows={3}
              name={'address'}
              label={t('streetAddress')}
            />
          </Grid>
          <Grid xs={12} md={7}>
            <FormInput {...formInputSharedProps} control={control} name={'email'} label={t('email') + '*'} />
          </Grid>
          <Grid xs={12} md={5}>
            <FormInput {...formInputSharedProps} control={control} name={'phoneNumber'} label={t('phone') + '*'} />
          </Grid>
          {shouldShowInvalidContactInfoWarning ? (
            <Grid xs={12} paddingTop={0}>
              <Alert severity={'error'}>{t('contactInvalidWarning')}</Alert>
            </Grid>
          ) : null}
          {shouldShowContactInfoWarning ? (
            <Grid xs={12} paddingTop={0}>
              <Alert severity={'warning'} sx={{ whiteSpace: 'break-spaces' }}>
                {t('contactChangeWarning')}
              </Alert>
            </Grid>
          ) : null}
          <Grid xs={12}>
            <FormSelect
              {...formInputSharedProps}
              control={control}
              name={'remindingTone'}
              label={t('remindingTone.title')}
              options={Object.values(RemindingTone).map((tone) => ({
                label: t(getRemindingToneTransKey(tone)),
                value: tone,
              }))}
            />
          </Grid>
          {shouldShowRemindingToneWarning ? (
            <Grid xs={12} paddingTop={0}>
              <Alert severity={'warning'}>{t('remindingTone.change.warning')}</Alert>
            </Grid>
          ) : null}
        </Grid>

        <Stack direction={'column'} gap={2} mt={6}>
          <LoadingButton
            {...submitButtonProps}
            startIcon={<Check />}
            fullWidth
            variant={'contained'}
            size={'large'}
            type={'submit'}
          >
            {t('saveContact')}
          </LoadingButton>
          <Button fullWidth variant={'text'} type={'button'} component={Link} to={'..'}>
            {t('cancel')}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
